import { zonedDate } from '../../../utils/date-time';
import moment from 'moment';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { Agendamento } from '../Enum';
import { CalendarCheck, Check, Crown, Hourglass, MessageSquare, Repeat, TrendingUp, X } from 'lucide-react';
import './ScheduleItem.css';
import type { LucideIcon } from 'lucide-react';

const EventSymbol = ({ icon: Icon, title }: { icon: LucideIcon; title?: string }) => (
    <span title={title} role="img" aria-label={title}>
        <Icon aria-hidden="true" />
    </span>
);

moment.locale('pt-br');

interface ScheduleItemProps {
    schedule: Agendamento;
    layout?: {
        heightPx?: number;
        topPx?: number;
        isMobileViewport?: boolean;
        stackedTop?: boolean;
        stackedBottom?: boolean;
    };
}

export default function ScheduleItem({ schedule, layout }: ScheduleItemProps) {
    const { setParams, action, id } = useParamsContext();
    const start = zonedDate(schedule.startAt, schedule.timezone);
    const end = zonedDate(schedule.endAt, schedule.timezone);
    const isSelected = action === 'view' && id === schedule._id;
    const subscription = schedule.subscription || null;
    const hasSubscription = Boolean(subscription?.assinaturaId || subscription?.planName);
    const isPreLinked = Boolean(subscription?.isPreLinked);
    const subscriptionLabel = hasSubscription
        ? subscription?.planName || 'Plano'
        : '';
    const subscriptionTitle = isPreLinked
        ? 'Assinatura pré-vinculada (período será confirmado na conclusão).'
        : hasSubscription
          ? `Plano: ${subscriptionLabel}`
          : '';
    const extrasCount = schedule.servicosExtras?.length || 0;
    const serviceLabel = extrasCount
        ? `${schedule.service.name} +${extrasCount}`
        : schedule.service.name;
    const saleOpportunity = schedule.saleOpportunity || null;
    const saleOpportunityEstimatedTotal = Number(saleOpportunity?.estimatedTotal || 0);
    const hasSaleOpportunity =
        Number(saleOpportunity?.activeRecurringCount || 0) > 0 || saleOpportunityEstimatedTotal > 0;
    const saleOpportunityValue = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(saleOpportunityEstimatedTotal);
    const opportunityFrequencyLabel = (saleOpportunity?.frequencies || [])
        .map((frequency) => {
            if (frequency === 'SEMANAL') return 'semanal';
            if (frequency === 'MENSAL') return 'mensal';
            return String(frequency || '').toLowerCase();
        })
        .filter(Boolean)
        .join(', ');
    const saleOpportunityTitle = hasSaleOpportunity
        ? [
              `${Number(saleOpportunity?.totalItems || 0)} item(ns)`,
              `Potencial ${saleOpportunityValue}`,
              opportunityFrequencyLabel ? `Frequência: ${opportunityFrequencyLabel}` : '',
          ]
              .filter(Boolean)
              .join(' · ')
        : '';
    const hasRecurrence = Boolean(schedule.recorrenciaId);
    const recurrenceTitle = hasRecurrence ? 'Gerado por recorrência' : '';
    const normalizedStatus = String(schedule.status || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    const isCompleted = normalizedStatus === 'concluido';
    const confirmationDate = schedule.confirmation?.date;
    const hasConfirmation = Boolean(confirmationDate);
    const isCancelled = normalizedStatus === 'cancelado';
    const observation = String(schedule.observation || '').trim();
    const hasObservation = Boolean(observation);

    const handleScheduleClick = () => {
        setParams({ action: 'view', id: schedule._id });
    };

    const heightPx = typeof layout?.heightPx === 'number' ? layout?.heightPx : null;
    const isMobileViewport = Boolean(layout?.isMobileViewport);
    const stackedTop = Boolean(layout?.stackedTop);
    const stackedBottom = Boolean(layout?.stackedBottom);

    // Size thresholds
    const tinyThreshold = isMobileViewport ? 84 : 72;
    const compactThreshold = isMobileViewport ? 112 : 96;
    const isTiny = heightPx !== null && heightPx < tinyThreshold;
    const isCompact = heightPx !== null && heightPx < compactThreshold;

    // Uma cor por status, como os calendarios do app Calendario.
    const tone = isCancelled
        ? 'gray'
        : normalizedStatus === 'pendente'
          ? 'orange'
          : isCompleted
            ? 'green'
            : 'blue';
    const statusLabel = isCancelled
        ? 'Cancelado'
        : normalizedStatus === 'pendente'
          ? 'Pendente'
          : isCompleted
            ? 'Concluído'
            : hasConfirmation
              ? 'Confirmado pelo cliente'
              : 'Agendado';
    const priceLabel = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(schedule?.totalPrice ?? schedule?.service?.price ?? 0);
    const timeLabel = `${start.format('HH:mm')}–${end.format('HH:mm')}`;
    const cancelledTitle = schedule.cancellation?.date
        ? `Cancelado ${moment(schedule.cancellation.date).fromNow()}`
        : 'Agendamento cancelado';
    // "Agendado" e o estado comum: so os demais ganham rotulo, alem da cor da aba.
    const statusBadge = isCancelled
        ? 'Cancelado'
        : normalizedStatus === 'pendente'
          ? 'Pendente'
          : isCompleted
            ? 'Concluído'
            : hasConfirmation
              ? 'Confirmado'
              : '';
    const showMetaRow = !isTiny && !isCompact && (hasSubscription || hasSaleOpportunity || Boolean(statusBadge));

    /* Simbolos monocromaticos ao lado do nome; em cards menores, assinatura e venda tambem viram simbolos. */
    const renderSymbols = () => (
        <span className="sf-event-symbols">
            {isCompleted && (
                <span title="Atendimento concluído" role="img" aria-label="Atendimento concluído">
                    <Check size={12} strokeWidth={3} aria-hidden="true" />
                </span>
            )}
            {hasConfirmation && !isCompleted && !isCancelled && (
                <span
                    title={`Cliente confirmou ${moment(confirmationDate).fromNow()}`}
                    role="img"
                    aria-label="Agendamento confirmado pelo cliente"
                >
                    <CalendarCheck size={12} strokeWidth={2.5} aria-hidden="true" />
                </span>
            )}
            {hasRecurrence && <EventSymbol icon={Repeat} title={recurrenceTitle} />}
            {(isTiny || isCompact) && hasSubscription && (
                <EventSymbol icon={isPreLinked ? Hourglass : Crown} title={subscriptionTitle} />
            )}
            {(isTiny || isCompact) && hasSaleOpportunity && (
                <EventSymbol icon={TrendingUp} title={saleOpportunityTitle} />
            )}
            {(isTiny || isCompact) && isCancelled && <EventSymbol icon={X} title={cancelledTitle} />}
        </span>
    );

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleScheduleClick}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleScheduleClick();
                }
            }}
            data-tone={tone}
            aria-label={`${schedule.client.name}, ${timeLabel}, ${serviceLabel}, ${statusLabel}`}
            aria-pressed={isSelected}
            className={[
                'sf-event',
                isTiny ? 'is-tiny' : isCompact ? 'is-compact' : '',
                isSelected ? 'is-selected' : '',
                isCancelled ? 'is-cancelled' : '',
                stackedTop ? 'is-stacked-top' : '',
                stackedBottom ? 'is-stacked-bottom' : '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {/* Aba com inicio e fim, na cor do status. */}
            <div className="sf-event-time">
                <span className="sf-event-start">{start.format('HH:mm')}</span>
                <span className="sf-event-end">{end.format('HH:mm')}</span>
            </div>

            <div className="sf-event-body">
                {/* Cliente, simbolos e valor: o nome trunca primeiro. */}
                <div className="sf-event-row">
                    <span className="sf-event-title" title={schedule.client.name}>
                        {schedule.client.name}
                    </span>
                    {renderSymbols()}
                    <span className="sf-event-price">{priceLabel}</span>
                </div>

                <span className="sf-event-secondary" title={serviceLabel}>
                    {serviceLabel}
                </span>

                {!isTiny && !isCompact && hasObservation && (
                    <div className="sf-event-note" title={observation}>
                        <MessageSquare className="text-[9px]" aria-hidden="true" />
                        <span>{observation}</span>
                    </div>
                )}

                {showMetaRow && (
                    <div className="sf-event-meta">
                        {statusBadge && (
                            <span className="sf-event-meta-item sf-event-status shrink-0" title={isCancelled ? cancelledTitle : statusLabel}>
                                <span>{statusBadge}</span>
                            </span>
                        )}
                        {hasSubscription && (
                            <span className="sf-event-meta-item" title={subscriptionTitle}>
                                {isPreLinked ? <Hourglass aria-hidden="true" /> : <Crown aria-hidden="true" />}
                                <span>{subscriptionLabel}</span>
                            </span>
                        )}
                        {hasSaleOpportunity && (
                            <span className="sf-event-meta-item shrink-0" title={saleOpportunityTitle}>
                                <TrendingUp aria-hidden="true" />
                                <span>{saleOpportunityValue}</span>
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
